import { routePath } from '@/lib/routes';
import { roadmapItem } from '@/lib/roadmap';

/**
 * Content for the /pricing page — tiers, usage dimensions, feature comparison
 * and FAQ. This is the single source of truth for plan numbers: /compare/*,
 * the homepage FAQ and the legal terms cite these same values — never fork
 * them into a second copy.
 *
 * Gating mirrors backend/src/api/services/plans.py — the only plan gates in
 * the product are active links, custom domains, team seats, API rate,
 * workflow node types (routing rules + dynamic responses are Premium) and
 * the dashboard assistant quota. Analytics, QR styling and the MCP server are
 * not plan-gated, so the matrix ticks them on every plan (truthful-claims
 * audit, Sept 2026). Email forwarding: simple forwarding routes (seeded
 * Trigger → Forward by services/email_routes.py, no plan check) work on Free;
 * editing an email workflow in the canvas is node-type-gated like any
 * workflow (`forward` isn't in the Free set), so that part is Premium.
 * Unbuilt roadmap rows carry the same dated tag on every plan — no tier
 * split is implied for features that don't exist yet. Enterprise domains/seats are
 * negotiated per contract ("Custom"), not unlimited by default.
 *
 * Pricing model (owner call, Aug 2026 round): meter what the customer
 * controls. Plans are gated on ACTIVE links (a standing cap — no monthly
 * resets), custom domains, seats, retention and API rate — never on traffic.
 * Tracked clicks are unmetered on every plan, including Free. The only
 * monthly number on the page is the price.
 *
 * Display currency is EUR (owner call, Aug 2026 round) — billing itself is
 * EUR too, see the currency FAQ entry.
 */

import { APP_URL, LOGIN_URL, REGISTER_URL } from '@/lib/app-url';

export { APP_URL, LOGIN_URL, REGISTER_URL };
export const SALES_MAILTO = 'mailto:sales@rii.link';
/** Primary Enterprise CTA everywhere (tier card, strip, estimator overflow);
 *  the mailto is a demoted fallback text link in the Enterprise strip. */
export const CONTACT_SALES_URL = `${routePath('contact')}?intent=sales`;
/** Premium is assigned by our team today (backend admin_tenants.py
 *  PATCH /{tenant_id}/plan, "until Stripe owns transitions") — there is no
 *  checkout, so the Premium CTA opens the contact form pre-set to a Premium
 *  request instead of pretending registration sells it. */
export const PREMIUM_REQUEST_URL = `${routePath('contact')}?intent=premium`;
/** Flip to true only when self-serve checkout ships; the claims sweep forbids
 *  proration / card / "cancel anytime" copy while this is false. */
export const SELF_SERVE_BILLING: boolean = false;

/** Display/billing currency symbol. */
export const CURRENCY = '€';

/** Roadmap features carry dated tags in the matrix — never checkmarks.
 *  Dates come from lib/roadmap.ts (#195), the sitewide single source; an
 *  item that ships there (MCP did, Aug 2026) leaves this object and its
 *  matrix row flips to real checkmarks. */
export const ROADMAP = {
  webhooks: roadmapItem('webhooks').promised.toUpperCase(),
  nameservers: roadmapItem('nameservers').promised.toUpperCase(),
} as const;

export type Billing = 'monthly' | 'annual';

export type Tier = {
  id: 'free' | 'premium' | 'enterprise';
  name: string;
  tagline: string;
  /** Monthly price per billing mode; null = custom pricing. */
  price: { monthly: number; annual: number } | null;
  /** Small line under the price, per billing mode. Keep both the same length-ish
   *  so card heights never shift when the toggle flips. */
  hint: { monthly: string; annual: string };
  cta: { label: string; href: string; variant: 'primary' | 'ghost' };
  highlight?: boolean;
  features: string[];
};

export const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For side projects and trying rii out.',
    price: { monthly: 0, annual: 0 },
    hint: {
      monthly: 'Free forever — no credit card.',
      annual: 'Free forever — no credit card.',
    },
    cta: { label: 'Start for free', href: REGISTER_URL, variant: 'ghost' },
    features: [
      '100 active links — nothing resets monthly',
      'Unmetered clicks & traffic',
      '2 custom domains + rii.link slugs',
      'Branded QR codes — logo & colors',
      'Analytics dashboard, 30-day retention',
      'REST API access — 60 req/min',
      'Email support',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'For creators, marketers and startups.',
    price: { monthly: 19, annual: 16 },
    hint: {
      monthly: `or ${CURRENCY}16/mo billed annually`,
      annual: `billed annually — ${CURRENCY}192/year`,
    },
    cta: { label: 'Request Premium', href: PREMIUM_REQUEST_URL, variant: 'primary' },
    highlight: true,
    features: [
      'Unlimited active links',
      '5 custom domains',
      'Smart routing — device, geo, language & time',
      'A/B splits & fallback chains',
      'Dynamic responses (JSON / HTML / XML)',
      '1-year analytics retention',
      'Full API access — 600 req/min',
      '3 team seats',
      'Priority email support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For teams that route serious traffic.',
    price: null,
    hint: {
      monthly: 'Custom volume & terms.',
      annual: 'Custom volume & terms.',
    },
    cta: { label: 'Talk to sales', href: CONTACT_SALES_URL, variant: 'ghost' },
    features: [
      'Custom domain limits',
      'Custom team seat limits',
      'Unlimited analytics retention',
      'Custom API rate limits',
      'Advanced routing & LLM responses at scale',
      'SSO / SAML on request & role-based access',
      '99.99% uptime SLA',
      'Dedicated support & onboarding',
    ],
  },
];

/** Self-serve plan limits the estimator matches against — same numbers the
 *  instrument band and comparison matrix print, no second copy. `links` is a
 *  standing active-link cap (Premium and up are unmetered). */
export const LIMITS = {
  links: { free: 100 },
  domains: { free: 2, premium: 5 },
} as const;

/** Steps for the estimator's active-links slider. */
export const EST_LINK_STEPS = [
  10, 25, 50, 100, 250, 500, 1_000, 2_500, 5_000, 10_000,
] as const;

export type UsageDimension = {
  term: string;
  copy: string;
  /** Free → Premium → Enterprise readout, joined with arrows in the UI. */
  steps: [string, string, string];
  /** Owner page for the dimension, shown as a trailing mono link. */
  href?: string;
  /** The celebrated non-limit (tracked clicks): rendered as a "never
   *  metered" cell rather than a tier progression. */
  nonLimit?: boolean;
};

/** The numbers that gate the plans — explained before the table uses them.
 *  Five gates plus the one deliberate non-gate (traffic). */
export const USAGE_DIMENSIONS: UsageDimension[] = [
  {
    term: 'Active links',
    copy: `Links that exist right now, across all your domains. Delete one and the slot frees up — nothing resets on the 1st. Premium and up don't count them at all.`,
    steps: [`${LIMITS.links.free}`, '∞', '∞'],
  },
  {
    term: 'Tracked clicks',
    copy: 'Counted for your analytics, never against you. Every plan — including Free — routes unlimited traffic. A viral day is a good day, not an invoice.',
    steps: ['∞', '∞', '∞'],
    href: routePath('analytics'),
    nonLimit: true,
  },
  {
    term: 'Custom domains',
    copy: "Domains you've pointed at rii.link — one DNS record each. Every domain hosts unlimited paths under it.",
    steps: [`${LIMITS.domains.free}`, `${LIMITS.domains.premium}`, 'CUSTOM'],
    href: routePath('customDomains'),
  },
  {
    term: 'Data retention',
    copy: 'The click history each plan keeps at minimum — nothing is purged early. The dashboard charts the last 24 hours, 7 or 30 days on every plan.',
    steps: ['30 DAYS', '1 YEAR', '∞'],
    href: routePath('analytics'),
  },
  {
    term: 'API rate limit',
    copy: 'Requests per minute your API key can make. Routing itself is never rate-limited — a redirect is not an API call.',
    steps: ['60', '600', 'CUSTOM REQ/MIN'],
    href: routePath('developers'),
  },
  {
    term: 'Team seats',
    copy: 'Logins with access to your workspace. Seats are not tied to link or traffic volume — a quiet account counts the same as a busy one.',
    steps: ['1', '3', 'CUSTOM'],
  },
];

/** `true` = included, `false` = not included, string = literal value,
 *  `{ tag }` = dated roadmap pill (amber mono — never a checkmark). */
export type Cell = boolean | string | { tag: string };

export type ComparisonGroup = {
  category: string;
  rows: {
    label: string;
    /** Page that owns the feature — row labels link to it. */
    href?: string;
    free: Cell;
    premium: Cell;
    enterprise: Cell;
  }[];
};

export const COMPARISON: ComparisonGroup[] = [
  {
    category: 'Links & Domains',
    rows: [
      { label: 'Active links (nothing resets monthly)', free: '100', premium: 'Unlimited', enterprise: 'Unlimited' },
      { label: 'Tracked clicks & traffic', href: routePath('analytics'), free: 'Unmetered', premium: 'Unmetered', enterprise: 'Unmetered' },
      { label: 'Custom domains', href: routePath('customDomains'), free: '2', premium: '5', enterprise: 'Custom' },
      { label: 'Branded QR codes (logo, colors)', href: routePath('qrCodes'), free: true, premium: true, enterprise: true },
      { label: 'Link expiration (API)', href: routePath('linkManagement'), free: true, premium: true, enterprise: true },
      { label: 'Email forwarding', free: true, premium: true, enterprise: true },
      { label: 'Nameserver delegation', href: routePath('customDomains'), free: { tag: ROADMAP.nameservers }, premium: { tag: ROADMAP.nameservers }, enterprise: { tag: ROADMAP.nameservers } },
    ],
  },
  {
    category: 'Smart Routing',
    rows: [
      { label: 'Routing rules per link', href: routePath('smartRouting'), free: false, premium: 'Unlimited', enterprise: 'Unlimited' },
      { label: 'Device, geo & time targeting', href: routePath('smartRouting'), free: false, premium: true, enterprise: true },
      { label: 'A/B split routing', href: routePath('smartRouting'), free: false, premium: true, enterprise: true },
      { label: 'Dynamic responses (JSON / HTML / XML)', href: routePath('dynamicResponses'), free: false, premium: true, enterprise: true },
      { label: 'Fallback / required-default chains', href: routePath('smartRouting'), free: false, premium: true, enterprise: true },
    ],
  },
  {
    category: 'Analytics',
    rows: [
      { label: 'Live click dashboard', href: routePath('analytics'), free: true, premium: true, enterprise: true },
      { label: 'Data retention', href: routePath('analytics'), free: '30 days', premium: '1 year', enterprise: 'Unlimited' },
      { label: 'Workspace data export (links + click log)', href: routePath('analytics'), free: true, premium: true, enterprise: true },
      { label: 'Campaign-tag (UTM) & referrer breakdowns', href: routePath('analytics'), free: true, premium: true, enterprise: true },
      { label: 'Device, browser, OS & country breakdowns', href: routePath('analytics'), free: true, premium: true, enterprise: true },
    ],
  },
  {
    category: 'API & Integrations',
    rows: [
      { label: 'REST API access (read + write)', href: routePath('developers'), free: true, premium: true, enterprise: true },
      { label: 'API rate limit', href: routePath('developers'), free: '60 req/min', premium: '600 req/min', enterprise: 'Custom' },
      { label: 'Webhooks', href: routePath('developers'), free: { tag: ROADMAP.webhooks }, premium: { tag: ROADMAP.webhooks }, enterprise: { tag: ROADMAP.webhooks } },
      { label: 'MCP server (agents & LLM tools)', href: routePath('aiMcp'), free: true, premium: true, enterprise: true },
      { label: 'Live click event stream (SSE, API-created links)', href: routePath('developers'), free: true, premium: true, enterprise: true },
    ],
  },
  {
    category: 'Team & Security',
    rows: [
      { label: 'Team seats', free: '1', premium: '3', enterprise: 'Custom' },
      { label: 'Roles & permissions (owner, admin, editor, viewer)', free: false, premium: true, enterprise: true },
      { label: 'SSO / SAML', href: routePath('solutionsEnterprise'), free: false, premium: false, enterprise: 'On request' },
      { label: 'Visitor IPs anonymised before storage', href: routePath('security'), free: true, premium: true, enterprise: true },
    ],
  },
  {
    category: 'Support',
    rows: [
      { label: 'Support channel', free: 'Email', premium: 'Priority email', enterprise: 'Dedicated + Slack' },
      { label: 'Uptime SLA', free: false, premium: false, enterprise: '99.99%' },
      { label: 'Onboarding & migration', free: false, premium: false, enterprise: true },
    ],
  },
];

export const PRICING_FAQS: { q: string; a: string }[] = [
  {
    q: 'How do I upgrade, downgrade or cancel?',
    a: 'Upgrades are set up by our team — start free and ask for Premium through the contact form, and we switch your workspace over. Downgrades and cancellations work the same way: tell us, and your workspace falls back to Free — your links keep working.',
  },
  {
    q: 'Can I export my data before I cancel?',
    a: "Yes, on every plan. Workspace owners and admins can download every link (JSON, CSV, or nginx and Caddy rule files) and the per-click request log (CSV or JSON) under Settings → Export; the request log reaches back as far as your plan's retention. Over the API, GET /v1/export/links and GET /v1/export/request-logs do the same for the links you created there. Cancelling never deletes or breaks links; they just fall back to Free limits, and your custom domain was always yours — re-point it whenever.",
  },
  {
    q: 'Do you limit clicks or traffic?',
    a: "No — on any plan, including Free. Every human visit rii answers — redirects and dynamic JSON/HTML responses alike — is counted for your analytics. Known bots remain visible in raw request logs but are excluded from the totals. Traffic is never a plan limit: there's no click quota, no overage, and nothing changes about your links on a viral day. The one honest caveat: analytics at truly extreme volume is an Enterprise conversation — fair use, not a meter.",
  },
  {
    q: 'What happens when I hit 100 active links on Free?',
    a: "Creating new links pauses until you delete one or upgrade — every existing link keeps routing, forever. The cap counts links that exist, not links you've ever made, so nothing resets monthly and deleting a retired campaign frees its slot immediately.",
  },
  {
    q: 'How does annual billing work?',
    a: `Annual Premium is ${CURRENCY}16/month billed once a year (${CURRENCY}192). That's about 16% less than paying monthly. Say which you prefer when you ask for Premium — our team sets the plan up for you.`,
  },
  {
    q: 'Can I bring my own custom domain?',
    a: 'Yes, on every plan. Point one DNS record at rii.link and your links and QR codes carry your brand. Free includes 2 domains, Premium 5, Enterprise a number set in your contract — and the domain is always yours to re-point.',
  },
  {
    q: 'What payment methods do you accept?',
    a: "There's no self-serve checkout yet — Premium is set up by our team, and we agree payment with you when you ask for it. Enterprise customers can pay by invoice (SEPA / bank transfer) with custom billing terms.",
  },
  {
    q: 'What currency will I be billed in?',
    a: 'Prices are set and billed in euros (EUR). If you pay from an account in another currency, your bank converts at its usual rate. A second display currency is on the table — billing itself stays EUR.',
  },
  {
    // Honest placeholder — do NOT assert VAT treatment until /legal/imprint
    // carries the real VAT ID.
    q: 'How is VAT handled for EU customers?',
    a: "An honest placeholder: our VAT registration is still being finalized, so we won't assert VAT treatment here yet — whether prices display inclusive or exclusive, or how reverse charge applies to EU B2B customers with a valid VAT ID. If tax treatment decides your purchase, ask via the contact form and a human will answer with specifics before you pay anything.",
  },
  {
    q: 'How does Enterprise onboarding work?',
    a: "Talk to sales and we'll scope volumes, SSO and data-handling needs together. We migrate your existing links for you and provide a dedicated onboarding engineer.",
  },
  {
    q: 'Is there a free trial of Premium?',
    a: 'The Free plan is the trial — no credit card, no time limit. Ask for Premium the moment you need smart routing, more domains or higher volume.',
  },
];
